import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Form,
  Label,
  Input,
  Row,
  Col,
  Spinner,
  Badge
} from 'reactstrap';
import moment from 'moment';
import { toast, Zoom } from 'react-toastify';
import {
  User,
  Calendar,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Coffee,
  Sun,
  Moon,
  Send,
  MapPin,
  Home,
  Phone,
  Layers,
  CheckSquare
} from 'react-feather';
import { Create_Meal_Pass, Get_Meal_Time_Slots } from '../../api/MealPassApi/MealPassApi';
import { DonarList, Add_Donar } from '../../api/Donar Api/DonarApi';
import { Donation_bill_Details } from '../../api/DonationBillApi/DonationCillapi';
import { DialCodeList } from '../../api/DialCodeApi/DialCodeApi';
import './MealPass.css';

const MAX_PERSONS = 5;

const CreateMealPassModal = ({ isOpen, toggle, onSuccess, selectedTrustId, trustList }) => {
  // Wizard Step: 1 = Devotee Details, 2 = Select Slot & Pass Generation
  const [activeStep, setActiveStep] = useState(1);

  // Trust state
  const [trustId, setTrustId] = useState(selectedTrustId || '');

  useEffect(() => {
    if (selectedTrustId) {
      setTrustId(selectedTrustId);
    } else if (trustList?.length > 0 && !trustId) {
      setTrustId(trustList[0].trustId);
    }
  }, [selectedTrustId, trustList, isOpen]);

  // Step 1: Devotee state
  const [dialCode, setDialCode] = useState('+91');
  const [contactNo, setContactNo] = useState('');
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [village, setVillage] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('India');
  const [address, setAddress] = useState('');
  const [personId, setPersonId] = useState(null);
  const [personToken, setPersonToken] = useState('');
  const [isExistingDevotee, setIsExistingDevotee] = useState(false);
  const [isSearchingDevotee, setIsSearchingDevotee] = useState(false);

  // Dial code list
  const dialCodes = useMemo(() => {
    try {
      return DialCodeList() || [];
    } catch {
      return [{ country: '(+91) India', code: '+91', iso: 'IN' }];
    }
  }, []);

  // Step 2: Slot & Date state
  const [noOfPerson, setNoOfPerson] = useState(1);
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  // mealSlot state: Array of { date: 'YYYY-MM-DD', mealSlot: [1, 2, 3] }
  const [mealSlotList, setMealSlotList] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([
    { mealTimeSlotId: 1, type: 'breakFast', label: 'Breakfast (अल्पाहार)', timeRange: '07:30 AM - 09:30 AM' },
    { mealTimeSlotId: 2, type: 'lunch', label: 'Lunch (दोपहर भोजन)', timeRange: '11:30 AM - 02:00 PM' },
    { mealTimeSlotId: 3, type: 'dinner', label: 'Dinner (रात्रि भोजन)', timeRange: '06:30 PM - 08:30 PM' }
  ]);
  const [sendWhatsApp, setSendWhatsApp] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [datePreset, setDatePreset] = useState('today');

  // Load available time slots when modal opens
  useEffect(() => {
    if (isOpen) {
      Get_Meal_Time_Slots()
        .then(({ data }) => {
          if (data?.payload?.slots && data.payload.slots.length > 0) {
            setAvailableSlots(
              data.payload.slots.map((s) => ({
                mealTimeSlotId: s.mealTimeSlotId,
                type: s.type,
                label:
                  s.type === 'breakFast'
                    ? 'Breakfast (अल्पाहार)'
                    : s.type === 'lunch'
                    ? 'Lunch (दोपहर भोजन)'
                    : 'Dinner (रात्रि भोजन)',
                timeRange: `${moment(s.startTime, 'HH:mm:ss').format('hh:mm A')} - ${moment(s.endTime, 'HH:mm:ss').format('hh:mm A')}`
              }))
            );
          }
        })
        .catch((err) => console.error('Error fetching meal time slots:', err));
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveStep(1);
      setContactNo('');
      setName('');
      setDob('');
      setVillage('');
      setCity('');
      setState('');
      setCountry('India');
      setAddress('');
      setPersonId(null);
      setPersonToken('');
      setIsExistingDevotee(false);
      setNoOfPerson(1);
      const today = moment().format('YYYY-MM-DD');
      setStartDate(today);
      setEndDate(today);
      setDatePreset('today');
      setMealSlotList([{ date: today, mealSlot: [1, 2, 3] }]);
    }
  }, [isOpen]);

  // Lookup devotee when contact number changes
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const cleanDigits = contactNo.replace(/\D/g, '');
    if (cleanDigits.length === 10) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(async () => {
        setIsSearchingDevotee(true);
        try {
          const mobileQuery = `${dialCode} ${cleanDigits}`;
          let foundPerson = null;
          let tokenStr = '';

          // 1. Try Donation_bill_Details
          try {
            const { data } = await Donation_bill_Details({ mobile: mobileQuery });
            if (data?.status === 200 && data.payload?.data) {
              foundPerson = data.payload.data;
              if (data.payload.personToken) {
                tokenStr = `Bearer ${data.payload.personToken}`;
              }
            }
          } catch (e) {
            console.warn('Donation_bill_Details lookup check:', e);
          }

          // 2. Fallback to DonarList search if not found
          if (!foundPerson) {
            try {
              const { data } = await DonarList({ search: cleanDigits, recordsPerPage: 5 });
              if (data?.status === 200 && data.payload?.data?.length > 0) {
                const match = data.payload.data.find(
                  (p) => String(p.mobile || '').replace(/\D/g, '').endsWith(cleanDigits)
                );
                if (match) {
                  foundPerson = match;
                }
              }
            } catch (e) {
              console.warn('DonarList lookup check:', e);
            }
          }

          if (foundPerson) {
            setIsExistingDevotee(true);
            setPersonId(foundPerson.personId);
            if (tokenStr) setPersonToken(tokenStr);
            setName(foundPerson.name || '');
            setVillage(foundPerson.village || '');
            setCity(foundPerson.city || '');
            setState(foundPerson.state || '');
            setCountry(foundPerson.country || 'India');
            setAddress(foundPerson.address || '');
            if (foundPerson.dob) {
              setDob(moment(foundPerson.dob).format('YYYY-MM-DD'));
            }
          } else {
            setIsExistingDevotee(false);
            setPersonId(null);
            setPersonToken('');
          }
        } catch (err) {
          console.error('Error querying devotee:', err);
          setIsExistingDevotee(false);
        } finally {
          setIsSearchingDevotee(false);
        }
      }, 350);
    } else {
      setIsExistingDevotee(false);
      setPersonId(null);
      setPersonToken('');
    }

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [contactNo, dialCode]);

  // Generate intermediate dates between startDate and endDate
  useEffect(() => {
    if (startDate && endDate) {
      const start = moment(startDate);
      const end = moment(endDate);
      if (end.isBefore(start)) {
        return;
      }

      const daysDiff = end.diff(start, 'days');
      if (daysDiff > 30) {
        toast.warning('Maximum range for a single Bhojan Pass is 30 days', {
          position: toast.POSITION.TOP_RIGHT,
          transition: Zoom
        });
        setEndDate(start.clone().add(29, 'days').format('YYYY-MM-DD'));
        return;
      }

      const generated = [];
      let cur = start.clone();
      while (cur.isSameOrBefore(end)) {
        const dStr = cur.format('YYYY-MM-DD');
        // Preserve previous selections for this date if already in state
        const existing = mealSlotList.find((item) => item.date === dStr);
        generated.push({
          date: dStr,
          mealSlot: existing ? existing.mealSlot : [1, 2, 3] // Default to all 3 slots
        });
        cur.add(1, 'days');
      }
      setMealSlotList(generated);
    }
  }, [startDate, endDate]);

  // Handle slot checkbox toggle for a specific date
  const handleSlotToggle = (dateStr, slotId) => {
    setMealSlotList((prev) =>
      prev.map((item) => {
        if (item.date !== dateStr) return item;
        const currentSlots = item.mealSlot || [];
        const hasSlot = currentSlots.includes(slotId);
        const updatedSlots = hasSlot
          ? currentSlots.filter((id) => id !== slotId)
          : [...currentSlots, slotId].sort((a, b) => a - b);
        return {
          ...item,
          mealSlot: updatedSlots
        };
      })
    );
  };

  // Toggle all slots for a single date
  const handleToggleDayAll = (dateStr) => {
    setMealSlotList((prev) =>
      prev.map((item) => {
        if (item.date !== dateStr) return item;
        const allSelected = item.mealSlot.length === availableSlots.length;
        return {
          ...item,
          mealSlot: allSelected ? [] : availableSlots.map((s) => s.mealTimeSlotId)
        };
      })
    );
  };

  // Bulk slot presets across all generated days
  const applyBulkSlots = (slotsToApply) => {
    setMealSlotList((prev) =>
      prev.map((item) => ({
        ...item,
        mealSlot: [...slotsToApply]
      }))
    );
  };

  // Date presets (Today, Tomorrow, 2 Days, 3 Days)
  const applyDatePreset = (preset) => {
    setDatePreset(preset);
    const today = moment().format('YYYY-MM-DD');
    if (preset === 'today') {
      setStartDate(today);
      setEndDate(today);
    } else if (preset === 'tomorrow') {
      const tom = moment().add(1, 'day').format('YYYY-MM-DD');
      setStartDate(tom);
      setEndDate(tom);
    } else if (preset === '2days') {
      setStartDate(today);
      setEndDate(moment().add(1, 'day').format('YYYY-MM-DD'));
    } else if (preset === '3days') {
      setStartDate(today);
      setEndDate(moment().add(2, 'days').format('YYYY-MM-DD'));
    }
  };

  // Stepper for person count (1 to 5)
  const handlePersonStep = (delta) => {
    setNoOfPerson((prev) => Math.max(1, Math.min(MAX_PERSONS, prev + delta)));
  };

  // Total meals calculation: sum of all selected slots across days * noOfPerson
  const totalMealServings = useMemo(() => {
    const totalSlotCount = mealSlotList.reduce((acc, curr) => acc + (curr.mealSlot ? curr.mealSlot.length : 0), 0);
    return totalSlotCount * Number(noOfPerson || 1);
  }, [mealSlotList, noOfPerson]);

  // Validate & Proceed to Step 2
  const handleProceedToStep2 = async (e) => {
    if (e) e.preventDefault();

    const cleanDigits = contactNo.replace(/\D/g, '');
    if (!cleanDigits || cleanDigits.length < 10) {
      toast.error('Please enter a valid 10-digit contact number', {
        position: toast.POSITION.TOP_RIGHT,
        transition: Zoom
      });
      return;
    }

    if (!name.trim()) {
      toast.error('Devotee Name is required', {
        position: toast.POSITION.TOP_RIGHT,
        transition: Zoom
      });
      return;
    }

    if (!village.trim()) {
      toast.error('Village / Native Place is required', {
        position: toast.POSITION.TOP_RIGHT,
        transition: Zoom
      });
      return;
    }

    if (!dob) {
      toast.error('Date of Birth is required', {
        position: toast.POSITION.TOP_RIGHT,
        transition: Zoom
      });
      return;
    }

    // If devotee is existing and has personId, move directly to step 2
    if (personId || personToken) {
      setActiveStep(2);
      return;
    }

    // If new devotee, register in Donar/PersonMaster
    setIsSubmitting(true);
    const regPayload = {
      name: name.trim(),
      mobile: `${dialCode} ${cleanDigits}`,
      dob: moment(dob).format('YYYY-MM-DD'),
      village: village.trim(),
      city: city.trim() || undefined,
      state: state.trim() || undefined,
      country: country.trim() || 'India',
      address: address.trim() || undefined
    };

    try {
      const { data } = await Add_Donar(regPayload);
      if (data?.status === 200 || data?.status === 201) {
        toast.success('Devotee registered successfully in temple records', {
          position: toast.POSITION.TOP_RIGHT,
          transition: Zoom
        });
        const createdPersonId = data.payload?.data?.personId || data.payload?.personId;
        if (createdPersonId) setPersonId(createdPersonId);
        if (data.payload?.personToken) setPersonToken(`Bearer ${data.payload.personToken}`);
        setIsExistingDevotee(true);
        setActiveStep(2);
      } else {
        // Devotee may already exist in records, lookup personId
        try {
          const lookup = await DonarList({ search: cleanDigits, recordsPerPage: 5 });
          if (lookup?.data?.payload?.data?.length > 0) {
            const match = lookup.data.payload.data.find(
              (p) => String(p.mobile || '').replace(/\D/g, '').endsWith(cleanDigits)
            );
            if (match) {
              setPersonId(match.personId);
              setIsExistingDevotee(true);
              setActiveStep(2);
              return;
            }
          }
        } catch (e) {
          console.warn('Fallback lookup error:', e);
        }
        setActiveStep(2);
      }
    } catch (err) {
      console.warn('Devotee registration fallback:', err);
      try {
        const lookup = await DonarList({ search: cleanDigits, recordsPerPage: 5 });
        if (lookup?.data?.payload?.data?.length > 0) {
          const match = lookup.data.payload.data.find(
            (p) => String(p.mobile || '').replace(/\D/g, '').endsWith(cleanDigits)
          );
          if (match) {
            setPersonId(match.personId);
            setIsExistingDevotee(true);
            setActiveStep(2);
            return;
          }
        }
      } catch (lookupErr) {
        console.warn('Lookup error:', lookupErr);
      }
      setActiveStep(2);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit and Issue Meal Pass
  const handleIssuePass = async () => {
    // Validate slot selection
    const activeSlots = mealSlotList.filter((item) => item.mealSlot && item.mealSlot.length > 0);
    if (activeSlots.length === 0) {
      toast.error('Please select at least one meal slot for any day', {
        position: toast.POSITION.TOP_RIGHT,
        transition: Zoom
      });
      return;
    }

    const cleanDigits = contactNo.replace(/\D/g, '');
    if (!cleanDigits && !personId) {
      toast.error('Devotee contact details are required. Please check Step 1.', {
        position: toast.POSITION.TOP_RIGHT,
        transition: Zoom
      });
      setActiveStep(1);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        noOfPerson: Number(noOfPerson),
        trustId: trustId ? Number(trustId) : undefined,
        mealSlot: activeSlots.map((item) => ({
          date: item.date,
          mealSlot: item.mealSlot
        })),
        devotee: {
          name: name.trim() || 'Devotee',
          mobile: `${dialCode} ${cleanDigits}`,
          village: village.trim() || undefined,
          city: city.trim() || undefined,
          state: state.trim() || undefined,
          country: country.trim() || 'India',
          address: address.trim() || undefined,
          dob: dob ? moment(dob).format('YYYY-MM-DD') : undefined
        }
      };

      if (personId) {
        payload.personId = personId;
      }
      if (personToken) {
        payload.personToken = personToken;
      }

      const { data } = await Create_Meal_Pass(payload);
      if (data?.status === 200) {
        toast.success(
          `Bhojan Pass issued successfully! ${totalMealServings} total meal servings scheduled.`,
          {
            position: toast.POSITION.TOP_RIGHT,
            transition: Zoom
          }
        );
        toggle();
        if (onSuccess) {
          onSuccess(data.payload);
        }
      } else {
        toast.error(data?.message || 'Failed to issue Bhojan Pass', {
          position: toast.POSITION.TOP_RIGHT,
          transition: Zoom
        });
      }
    } catch (err) {
      console.error('Error creating meal pass:', err);
      const msg = err.response?.data?.message || err.message || 'Error occurred while issuing pass';
      toast.error(msg, {
        position: toast.POSITION.TOP_RIGHT,
        transition: Zoom
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      onClosed={() => {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
      }}
      size="lg"
      backdrop="static"
      centered
      className="meal-pass-modal"
    >
      <ModalHeader toggle={toggle} className="border-bottom pb-3">
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: '#eff6ff',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Coffee size={20} />
          </div>
          <div>
            <h5 className="modal-title mb-0 fw-bold">Issue Bhojan Pass (भोजन पास जारी करें)</h5>
            <small className="text-muted">Generate digital QR meal pass for temple bhojanshala</small>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="p-4">
        {/* 2-Step Wizard Progress Bar */}
        <div className="wizard-stepper-header">
          {/* Step 1 Node */}
          <div
            className={`wizard-step-node ${activeStep === 1 ? 'active' : 'completed'}`}
            onClick={() => setActiveStep(1)}
          >
            <div className="wizard-step-circle">
              {activeStep > 1 ? <CheckCircle size={18} /> : 1}
            </div>
            <span className="wizard-step-title">1. Devotee Details (भक्त विवरण)</span>
          </div>

          {/* Step 2 Node */}
          <div
            className={`wizard-step-node ${activeStep === 2 ? 'active' : ''}`}
            onClick={() => {
              if (personId) {
                setActiveStep(2);
              } else if (contactNo.replace(/\D/g, '').length === 10 && name.trim()) {
                handleProceedToStep2();
              }
            }}
          >
            <div className="wizard-step-circle">2</div>
            <span className="wizard-step-title">2. Select Slots (समय व भोजन)</span>
          </div>
        </div>

        {/* STEP 1: Devotee Registration & Lookup (DonarForm flow) */}
        {activeStep === 1 && (
          <Form onSubmit={handleProceedToStep2}>
            {/* Temple Trust Selection */}
            {trustList && trustList.length > 0 && (
              <div className="p-3 mb-3 bg-light rounded-3 border">
                <Row className="align-items-center">
                  <Col md="4">
                    <Label className="fw-semibold text-dark mb-0 d-flex align-items-center">
                      <Layers size={15} className="me-1 text-primary" /> Temple Trust (मंदिर ट्रस्ट):
                    </Label>
                  </Col>
                  <Col md="8">
                    <Input
                      type="select"
                      value={trustId}
                      onChange={(e) => setTrustId(e.target.value)}
                      disabled={trustList.length === 1}
                      className="form-select fw-semibold"
                    >
                      {trustList.length > 1 && (
                        <option value="">Select Trust (ट्रस्ट चुनें)</option>
                      )}
                      {trustList.map((t) => (
                        <option key={t.trustId} value={t.trustId}>
                          {t.trustName}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>
              </div>
            )}

            {/* Contact Lookup Card */}
            <div className="devotee-lookup-card">
              <Row className="align-items-center">
                <Col md="7">
                  <Label className="fw-semibold text-dark mb-1">
                    <Phone size={14} className="me-1 text-primary" /> Contact Number (मोबाइल नंबर) *
                  </Label>
                  <div className="d-flex gap-2">
                    <Input
                      type="select"
                      value={dialCode}
                      onChange={(e) => setDialCode(e.target.value)}
                      style={{ width: '130px', fontWeight: '500' }}
                    >
                      {dialCodes.map((item, idx) => (
                        <option key={idx} value={item.code}>
                          {item.country}
                        </option>
                      ))}
                    </Input>
                    <div className="position-relative flex-grow-1">
                      <Input
                        type="text"
                        placeholder="Enter 10-digit mobile"
                        value={contactNo}
                        maxLength={10}
                        onChange={(e) => setContactNo(e.target.value.replace(/\D/g, ''))}
                        className="fw-semibold"
                        required
                        autoFocus
                      />
                      {isSearchingDevotee && (
                        <div
                          className="position-absolute top-50 end-0 translate-middle-y me-3 text-primary"
                          style={{ pointerEvents: 'none' }}
                        >
                          <Spinner size="sm" />
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
                <Col md="5" className="pt-3 pt-md-0 text-md-end">
                  {isSearchingDevotee ? (
                    <small className="text-muted d-block">Searching records...</small>
                  ) : isExistingDevotee ? (
                    <span className="devotee-status-badge registered">
                      <CheckCircle size={13} /> Registered Devotee
                    </span>
                  ) : contactNo.length >= 10 ? (
                    <span className="devotee-status-badge new">
                      <User size={13} /> New Devotee Registration
                    </span>
                  ) : (
                    <small className="text-muted d-block">Enter 10 digits to auto-search</small>
                  )}
                </Col>
              </Row>
            </div>

            {/* Devotee Info Form Fields */}
            <Row className="g-3">
              <Col md="6">
                <Label className="fw-semibold mb-1">
                  <User size={14} className="me-1 text-secondary" /> Devotee Name (पूरा नाम) *
                </Label>
                <Input
                  type="text"
                  placeholder="e.g. Ramesh Patel"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </Col>

              <Col md="6">
                <Label className="fw-semibold mb-1">
                  <Calendar size={14} className="me-1 text-secondary" /> Date of Birth (जन्म तिथि) *
                </Label>
                <Input
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  max={moment().format('YYYY-MM-DD')}
                  required
                />
              </Col>

              <Col md="6">
                <Label className="fw-semibold mb-1">
                  <Home size={14} className="me-1 text-secondary" /> Village / Native Place (गाँव / वतन) *
                </Label>
                <Input
                  type="text"
                  placeholder="e.g. Gadhada / Jetpur"
                  value={village}
                  onChange={(e) => setVillage(e.target.value)}
                  required
                />
              </Col>

              <Col md="3">
                <Label className="fw-semibold mb-1">City (शहर)</Label>
                <Input
                  type="text"
                  placeholder="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </Col>

              <Col md="3">
                <Label className="fw-semibold mb-1">State (राज्य)</Label>
                <Input
                  type="text"
                  placeholder="State"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
              </Col>

              <Col md="4">
                <Label className="fw-semibold mb-1">Country (देश)</Label>
                <Input
                  type="text"
                  placeholder="Country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </Col>

              <Col md="8">
                <Label className="fw-semibold mb-1">
                  <MapPin size={14} className="me-1 text-secondary" /> Address (पता)
                </Label>
                <Input
                  type="text"
                  placeholder="Street / Society address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </Col>
            </Row>

            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top modal-action-footer">
              <Button color="light" onClick={toggle}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={isSubmitting} className="d-flex align-items-center gap-2 px-4">
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" /> Registering...
                  </>
                ) : (
                  <>
                    Continue to Slot Selection <ArrowRight size={16} />
                  </>
                )}
              </Button>
            </div>
          </Form>
        )}

        {/* STEP 2: Slot Selection & Dates (SelectSlot flow) */}
        {activeStep === 2 && (
          <div>
            {/* Devotee Summary Bar */}
            <div className="p-3 mb-3 rounded-3 bg-light border d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="fw-bold text-dark">{name}</span>
                <span className="text-muted">| {dialCode} {contactNo}</span>
                {village && <span className="badge bg-secondary-subtle text-secondary">{village}</span>}
                {trustList?.find((t) => String(t.trustId) === String(trustId))?.trustName && (
                  <span className="badge bg-primary-subtle text-primary border">
                    🏛️ {trustList.find((t) => String(t.trustId) === String(trustId)).trustName}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-link text-primary text-decoration-none p-0 fw-semibold"
                onClick={() => setActiveStep(1)}
              >
                Change Devotee / Trust
              </button>
            </div>

            {/* Pass Parameters Row */}
            <Row className="g-3 mb-4">
              {/* Number of Persons */}
              <Col md="4">
                <Label className="fw-semibold text-dark mb-1">
                  Number of Diners (भोजनार्थी)
                </Label>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="counter-stepper">
                    <button
                      type="button"
                      disabled={noOfPerson <= 1}
                      onClick={() => handlePersonStep(-1)}
                    >
                      -
                    </button>
                    <input type="text" readOnly value={noOfPerson} />
                    <button
                      type="button"
                      disabled={noOfPerson >= MAX_PERSONS}
                      onClick={() => handlePersonStep(1)}
                    >
                      +
                    </button>
                  </div>
                  <div className="d-flex gap-1 flex-wrap">
                    {[1, 2, 3, 4, 5].map((num) => (
                      <button
                        key={num}
                        type="button"
                        className={`btn btn-sm ${noOfPerson === num ? 'btn-primary' : 'btn-light border'} px-2 py-1`}
                        onClick={() => setNoOfPerson(num)}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>
                <small className="text-muted d-block mt-1">Maximum {MAX_PERSONS} persons per pass</small>
              </Col>

              {/* Start Date & End Date */}
              <Col md="8">
                <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-1">
                  <Label className="fw-semibold text-dark mb-0">
                    <Calendar size={14} className="me-1 text-primary" /> Validity Range (दिनांक सीमा)
                  </Label>
                  <div className="d-flex gap-1 flex-wrap">
                    <button
                      type="button"
                      className={`preset-chip-btn ${datePreset === 'today' ? 'active' : ''}`}
                      onClick={() => applyDatePreset('today')}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      className={`preset-chip-btn ${datePreset === 'tomorrow' ? 'active' : ''}`}
                      onClick={() => applyDatePreset('tomorrow')}
                    >
                      Tomorrow
                    </button>
                    <button
                      type="button"
                      className={`preset-chip-btn ${datePreset === '2days' ? 'active' : ''}`}
                      onClick={() => applyDatePreset('2days')}
                    >
                      2 Days
                    </button>
                    <button
                      type="button"
                      className={`preset-chip-btn ${datePreset === '3days' ? 'active' : ''}`}
                      onClick={() => applyDatePreset('3days')}
                    >
                      3 Days
                    </button>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setDatePreset('custom');
                      setStartDate(e.target.value);
                      if (moment(e.target.value).isAfter(moment(endDate))) {
                        setEndDate(e.target.value);
                      }
                    }}
                    required
                    style={{ flex: '1 1 140px' }}
                  />
                  <span className="text-muted fw-bold">to</span>
                  <Input
                    type="date"
                    value={endDate}
                    min={startDate}
                    onChange={(e) => {
                      setDatePreset('custom');
                      setEndDate(e.target.value);
                    }}
                    required
                    style={{ flex: '1 1 140px' }}
                  />
                </div>
              </Col>
            </Row>

            {/* Bulk Slot Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-2 pt-2 border-top flex-wrap gap-2">
              <span className="fw-bold text-dark fs-6 d-flex align-items-center gap-1">
                <Layers size={16} className="text-primary" /> Schedule Slots per Day (दैनिक भोजन चयन)
              </span>
              <div className="d-flex gap-1 flex-wrap">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary py-1"
                  onClick={() => applyBulkSlots([1, 2, 3])}
                >
                  <CheckSquare size={13} className="me-1" /> All Meals (3 Meals)
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary py-1"
                  onClick={() => applyBulkSlots([2, 3])}
                >
                  Lunch & Dinner
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary py-1"
                  onClick={() => applyBulkSlots([1])}
                >
                  Breakfast Only
                </button>
              </div>
            </div>

            {/* Generated Date Cards */}
            <div style={{ maxHeight: '280px', overflowY: 'auto', paddingRight: '4px' }}>
              {mealSlotList.map((dayItem) => {
                const dayMoment = moment(dayItem.date);
                const isSelectedAll = dayItem.mealSlot?.length === availableSlots.length;

                return (
                  <div key={dayItem.date} className="day-slot-box">
                    <div className="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                      <div className="day-slot-title">
                        <Badge color="primary" className="fw-bold">
                          {dayMoment.format('ddd')}
                        </Badge>
                        <span>{dayMoment.format('DD MMMM, YYYY')}</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-muted text-decoration-none p-0"
                        onClick={() => handleToggleDayAll(dayItem.date)}
                      >
                        {isSelectedAll ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                      {/* Breakfast */}
                      <div
                        className={`slot-toggle-button ${dayItem.mealSlot?.includes(1) ? 'active-breakfast' : ''}`}
                        onClick={() => handleSlotToggle(dayItem.date, 1)}
                      >
                        <Coffee size={15} />
                        <span>Breakfast (अल्पाहार)</span>
                        <input
                          type="checkbox"
                          className="form-check-input ms-1"
                          checked={dayItem.mealSlot?.includes(1)}
                          onChange={() => {}} // handled by div click
                        />
                      </div>

                      {/* Lunch */}
                      <div
                        className={`slot-toggle-button ${dayItem.mealSlot?.includes(2) ? 'active-lunch' : ''}`}
                        onClick={() => handleSlotToggle(dayItem.date, 2)}
                      >
                        <Sun size={15} />
                        <span>Lunch (दोपहर भोजन)</span>
                        <input
                          type="checkbox"
                          className="form-check-input ms-1"
                          checked={dayItem.mealSlot?.includes(2)}
                          onChange={() => {}}
                        />
                      </div>

                      {/* Dinner */}
                      <div
                        className={`slot-toggle-button ${dayItem.mealSlot?.includes(3) ? 'active-dinner' : ''}`}
                        onClick={() => handleSlotToggle(dayItem.date, 3)}
                      >
                        <Moon size={15} />
                        <span>Dinner (रात्रि भोजन)</span>
                        <input
                          type="checkbox"
                          className="form-check-input ms-1"
                          checked={dayItem.mealSlot?.includes(3)}
                          onChange={() => {}}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Live Calculation Bar */}
            <div className="pass-calc-bar">
              <div>
                <span className="text-secondary small d-block">Pass Configuration:</span>
                <strong>
                  {noOfPerson} {noOfPerson === 1 ? 'Person' : 'Persons'} &times; {mealSlotList.length} {mealSlotList.length === 1 ? 'Day' : 'Days'}
                </strong>
              </div>
              <div className="text-end">
                <span className="text-secondary small d-block">Total Meals to be Served:</span>
                <span className="fs-5 fw-bold text-warning">{totalMealServings} Meals (थाली)</span>
              </div>
            </div>

            {/* WhatsApp notification toggle */}
            <div className="mt-3 form-check form-switch d-flex align-items-center gap-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="sendWhatsAppToggle"
                checked={sendWhatsApp}
                onChange={(e) => setSendWhatsApp(e.target.checked)}
              />
              <label className="form-check-label small text-muted" htmlFor="sendWhatsAppToggle">
                <Send size={13} className="text-success me-1" />
                Dispatch digital QR ticket via WhatsApp to devotee ({dialCode} {contactNo})
              </label>
            </div>

            {/* Footer Navigation */}
            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top modal-action-footer">
              <Button color="light" onClick={() => setActiveStep(1)} className="d-flex align-items-center gap-1">
                <ArrowLeft size={16} /> Back to Devotee Info
              </Button>
              <Button
                color="primary"
                onClick={handleIssuePass}
                disabled={isSubmitting || totalMealServings === 0}
                className="d-flex align-items-center gap-2 px-4 fw-bold"
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" /> Generating Pass...
                  </>
                ) : (
                  <>
                    <Coffee size={16} /> Issue Bhojan Pass ({totalMealServings} Meals)
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default CreateMealPassModal;
